import styled from "styled-components";
import Calendar from "../../Inputs/Calendar";
import React, { useEffect, useRef, useState } from "react";
import Dropdown from "../../Dropdown";
import Button, { TButtonIconVariants, TButtonProps } from "../../Button";
import { format } from "date-fns/format";
import { add } from "date-fns/add";
import { parseISO } from "date-fns/parseISO";
import {
  invoiceStatusOptions,
  natureOptions,
  TStatus,
} from "../../../../pages/IssuedInvoices/mock";
import ToggleSwitch from "../../Inputs/ToggleSwitch";
import { subMonths } from "date-fns/subMonths";
import { startOfMonth } from "date-fns/startOfMonth";
import { endOfMonth } from "date-fns/endOfMonth";
import {
  TIssuedExportedInvoice,
  TNature,
} from "../../../../pages/IssuedInvoices/Export/mock";

const Container = styled.form`
  & > p,
  label,
  b {
    font-size: 16px;
    color: var(--color-text-paragraph);
    font-family: var(--font-paragraph-default);
  }

  b {
    font-family: var(--font-paragraph-bold);
  }

  label {
    font-size: 14px;
  }

  table {
    thead tr:first-child {
      th b {
        font-size: 14px;
      }
    }

    thead tr:nth-child(2) {
      th b {
        font-size: 12px;
      }
    }
  }
`;

const RowContainer = styled.div<{
  $hasModeError?: "hasError" | "noError";
  $isPaid?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  margin-top: 16px;

  position: relative;

  h2 {
    font-size: 14px;
    font-family: var(--font-paragraph-bold);
    color: var(--color-text-paragraph);
  }

  .advanced-filter-container {
    margin-top: 8px;

    display: flex;
    gap: 16px;

    button {
      width: 522px;
    }

    menu {
      width: 522px;
      & button {
        width: 504px;
      }
    }

    & > div:first-child {
      width: 146px;

      & div button {
        p.content {
          top: 14px;
        }

        width: 146px;
      }
    }
  }
`;

const Footer = styled.footer`
  margin-top: 30px;
  border-top: 1px solid var(--color-light-200);
  padding: 24px 0px 0px;
  display: flex;
  justify-content: flex-end;

  .textContainer {
    b,
    p {
      font-size: 14px;
      color: var(--color-text-paragraph);
    }

    b {
      font-family: var(--font-paragraph-bold);
    }

    p {
      font-family: var(--font-paragraph-default);
    }
  }

  .buttonsContainer {
    display: flex;
    justify-content: flex-end;

    & button:not(& button:last-child) {
      margin-right: 12px;
    }
  }
`;

type TExportIssuedInvoiceProps = {
  invoice?: TIssuedExportedInvoice;
  footer: { buttons: TButtonProps<TButtonIconVariants>[] };
};

const ExportIssuedInvoice: React.FC<TExportIssuedInvoiceProps> = ({
  invoice,
  footer,
}) => {
  const exportedInvoiceRef = useRef<TIssuedExportedInvoice | undefined>(
    invoice
  );
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const today = new Date();
  const minDate = format(add(today, { days: -180 }), "dd/MM/yyyy");
  const maxDate = format(today, "dd/MM/yyyy");

  const [includeAll, setIncludeAll] = useState({
    status: true,
    nature: true,
  });

  // Example: initial state for status and natureza dropdowns
  const [selectedStatus, setSelectedStatus] = useState<TStatus[]>(
    exportedInvoiceRef.current?.status || []
  );
  const [selectedNature, setSelectedNature] = useState<TNature[]>(
    exportedInvoiceRef.current?.nature || []
  );

  // Handler for toggling a specific filter section
  const handleToggle = (key: keyof typeof includeAll, value: boolean) => {
    setIncludeAll((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (exportedInvoiceRef.current?.start_date) {
      setStartDate(
        format(parseISO(exportedInvoiceRef.current.start_date), "dd/MM/yyyy")
      );
    } else {
      const lastMonth = subMonths(new Date(), 1);
      setStartDate(format(startOfMonth(lastMonth), "dd/MM/yyyy"));
    }
    if (exportedInvoiceRef.current?.end_date) {
      setEndDate(
        format(parseISO(exportedInvoiceRef.current.end_date), "dd/MM/yyyy")
      );
    } else {
      const lastMonth = subMonths(new Date(), 1);
      setEndDate(format(endOfMonth(lastMonth), "dd/MM/yyyy"));
    }
  }, [invoice]);

  useEffect(() => {
    if (invoice) {
      setIncludeAll({
        status: invoice?.status ? invoice.status.length === 0 : true,
        nature: invoice?.nature ? invoice.nature.length === 0 : true,
      });
    }
  }, []);

  return (
    <Container
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <RowContainer style={{ marginTop: "0px" }}>
        <Calendar
          key={startDate + endDate}
          variant="period"
          placeholder="Selecionar período"
          hasMandatoryStyle={{ marginLeft: "127px" }}
          className="calendar"
          filled={{ startDate, endDate }}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={(_selectDate) => {
            // dueDate
          }}
        />
      </RowContainer>
      <RowContainer>
        <h2>Status</h2>

        <div style={{ marginTop: "8px" }}>
          <ToggleSwitch
            key={includeAll.status + ""}
            id="automation-process-status-selector"
            initialState={includeAll.status}
            onClick={(value) => handleToggle("status", value)}
          />
          <label htmlFor="automation-process-status-selector">
            &nbsp;Todos os status &nbsp;
          </label>
        </div>
        {!includeAll.status && (
          <div className="advanced-filter-container">
            <Dropdown
              button={{
                variant: "unlabeled",
                labels: ["Iguais a", "Diferentes de"],
                placeholder: "",
                //
              }}
              menu={{
                variant: "unlabeled",
                options: ["Iguais a", "Diferentes de"],
                search: false,
                shouldHideActiveIcon: true,
                onSelect(_selected) {
                  //
                },
              }}
            />
            <Dropdown
              key={selectedStatus.length}
              button={{
                selectType: "multi",
                variant: "labeled",
                labels: [],
                placeholder: "Selecionar status",
                initialMultiSelectLabels: selectedStatus,
                onRemove(_removedTag, updatedSelected) {
                  setSelectedStatus(updatedSelected as TStatus[]);
                },
              }}
              menu={{
                variant: "unlabeled",
                options: invoiceStatusOptions,
                search: false,
                shouldHideActiveIcon: true,
                forceSelectOptionsByIndex: selectedStatus
                  ? selectedStatus
                      .map((status) => invoiceStatusOptions.indexOf(status))
                      .filter((idx) => idx !== -1)
                  : [],
                onSelect(_selected) {
                  //
                },
              }}
            />
          </div>
        )}
      </RowContainer>
      <RowContainer>
        <h2>Natureza</h2>

        <div style={{ marginTop: "8px" }}>
          <ToggleSwitch
            key={includeAll.nature + ""}
            id="automation-process-nature-selector"
            initialState={includeAll.nature}
            onClick={(value) => handleToggle("nature", value)}
          />
          <label htmlFor="automation-process-nature-selector">
            &nbsp;Todas as naturezas de operação &nbsp;
          </label>
        </div>
        {!includeAll.nature && (
          <div className="advanced-filter-container">
            <Dropdown
              button={{
                variant: "unlabeled",
                labels: ["Iguais a", "Diferentes de"],
                placeholder: "",
                //
              }}
              menu={{
                variant: "unlabeled",
                options: ["Iguais a", "Diferentes de"],
                search: false,
                shouldHideActiveIcon: true,
                onSelect(_selected) {
                  //
                },
              }}
            />
            <Dropdown
              key={selectedNature.length}
              button={{
                selectType: "multi",
                variant: "labeled",
                labels: [],
                placeholder: "Selecionar natureza",
                initialMultiSelectLabels: selectedNature,
                onRemove(_removedTag, updatedSelected) {
                  setSelectedNature(updatedSelected as TNature[]);
                },
              }}
              menu={{
                variant: "unlabeled",
                options: natureOptions,
                search: false,
                shouldHideActiveIcon: true,
                forceSelectOptionsByIndex: selectedNature
                  ? selectedNature
                      .map((nature) => natureOptions.indexOf(nature))
                      .filter((idx) => idx !== -1)
                  : [],
                onSelect(_selected) {
                  //
                },
              }}
            />
          </div>
        )}
      </RowContainer>
      <Footer>
        <div className="buttonsContainer">
          {footer.buttons?.map((buttonProps, index) => {
            const { onClick, ...props } = buttonProps;
            return (
              <Button
                key={index}
                role={index === 1 ? "submit" : undefined}
                {...props}
              />
            );
          })}
        </div>
      </Footer>
    </Container>
  );
};

export default ExportIssuedInvoice;
